import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { PRODUTO_CONFIG } from '../config/produto';
import { ControlDate } from '../types/controle-datas';
import { getPeriodoFase, PeriodoFase } from '../utils/cronograma';

/**
 * Hook para buscar uma data de controle específica por _id.
 * Aceita id undefined para facilitar acesso a chaves opcionais de PhaseIds.
 *
 * @param id - O _id da data de controle a ser buscada (ou undefined)
 * @returns A data de controle encontrada ou undefined
 *
 * @example
 * const etapaPreElbb = useControlDate('etapa_pre_elbb');
 * if (etapaPreElbb) {
 *   console.log(etapaPreElbb.titulo, etapaPreElbb.dataInicio);
 * }
 */
export function useControlDate(id: string | undefined): ControlDate | undefined {
  const { controlDates } = useAuth();

  const controlDate = useMemo(() => {
    if (!id || !controlDates || controlDates.length === 0) {
      return undefined;
    }

    return controlDates.find((date) => date._id === id);
  }, [controlDates, id]);

  return controlDate;
}

/**
 * Início e fim de uma fase da controle_datas__c, ou null sem as duas datas válidas.
 *
 * @example
 * const periodo = usePeriodoFase(PHASE_IDS.ETAPA_1_PREMIO);
 * formatarPeriodo(periodo); // "01/04 a 15/04"
 */
export function usePeriodoFase(id: string | undefined): PeriodoFase | null {
  const controlDate = useControlDate(id);
  return useMemo(() => getPeriodoFase(controlDate), [controlDate]);
}

/**
 * Hook para buscar múltiplas datas de controle por _ids
 *
 * @param ids - Array de _ids das datas de controle
 * @returns Array de datas de controle encontradas
 *
 * @example
 * const [etapaPreElbb, etapaElbb] = useControlDates(['etapa_pre_elbb', 'etapa_elbb']);
 */
export function useControlDates(ids: string[]): (ControlDate | undefined)[] {
  const { controlDates } = useAuth();

  const dates = useMemo(() => {
    if (!controlDates || controlDates.length === 0) {
      return ids.map(() => undefined);
    }

    return ids.map((id) => controlDates.find((date) => date._id === id));
  }, [controlDates, ids]);

  return dates;
}

/**
 * Hook para verificar se uma data de controle está ativa.
 * Retorna false quando id é undefined (fase inexistente no produto atual).
 *
 * @param id - O _id da data de controle, ou undefined
 * @returns true se a data está ativa, false caso contrário
 *
 * @example
 * const inscricoesAbertas = useIsDateActive('etapa_pre_elbb');
 */
export function useIsDateActive(id: string | undefined): boolean {
  const controlDate = useControlDate(id);

  return useMemo(() => {
    if (!controlDate) return false;

    const now = new Date();
    const inicio = new Date(controlDate.dataInicio);
    const fim = controlDate.dataFim ? new Date(controlDate.dataFim) : null;

    if (fim) {
      return now >= inicio && now <= fim;
    }

    return now >= inicio;
  }, [controlDate]);
}

/**
 * Structured result of all phase states.
 * Each boolean tells you if that specific phase is currently active.
 *
 * elbbVotingStage1 / elbbVotingStage2: flags de votação ELBB; false no build Prêmio BB.
 * etapa1premio / etapa2premio: flags do Prêmio BB; false no build Prêmio ELBB.
 */
export interface ActivePhases {
  // ELBB
  elbbPreRegistration: boolean;
  elbbRegistrationPeriod: boolean;
  elbbConfirmation: boolean;
  elbbInscriptionPeriod: boolean; // Adicionado para controle de período de inscrição do ELBB
  elbbVotingStage1: boolean; // Fase de votação popular ELBB (etapa 1)
  elbbVotingStage2: boolean; // Fase de votação popular ELBB (etapa 2)
  // Conexao / Prêmio BB
  conexaoPreRegistration: boolean;
  conexaoRegistrationPeriod: boolean;
  conexaoManagerApproval: boolean;
  conexaoConfirmation: boolean;
  etapa1premio: boolean;
  etapa2premio: boolean;
  etapa3premio: boolean;
  // Derived helpers
  isAnyElbbActive: boolean;
  canAccessCadastroELBB: boolean;
  isAnyConexaoActive: boolean;
  canAccessCadastroConexao: boolean;
}

/**
 * Hook that returns all phase states at once.
 * Uses PRODUTO_CONFIG.phaseIds so the correct IDs are used per product.
 * Call once at the routing/page level, pass down or use directly.
 *
 * @example
 * const phases = useActivePhases();
 * if (phases.elbbRegistrationPeriod) { ... }
 * if (phases.conexaoPreRegistration) { ... }
 */
export function useActivePhases(): ActivePhases {
  const phaseIds = PRODUTO_CONFIG.phaseIds;

  // ELBB
  const elbbPreRegistration = useIsDateActive(phaseIds['ELBB_PRE_REGISTRATION']);
  const elbbRegistrationPeriod = useIsDateActive(phaseIds['ELBB_REGISTRATION_PERIOD']);
  const elbbConfirmation = useIsDateActive(phaseIds['ELBB_CONFIRMATION']);
  const elbbInscriptionPeriod = useIsDateActive(phaseIds['ELBB_INSCRIPTION']);
  // ELBB voting stages — undefined em PREMIO_BB → false
  const elbbVotingStage1 = useIsDateActive(phaseIds['ELBB_VOTING_STAGE1']);
  const elbbVotingStage2 = useIsDateActive(phaseIds['ETAPA_2_PREMIO']);
  // Conexao / Prêmio BB
  const conexaoPreRegistration = useIsDateActive(phaseIds['CONEXAO_PRE_REGISTRATION']);
  const conexaoRegistrationPeriod = useIsDateActive(phaseIds['CONEXAO_REGISTRATION_PERIOD']);
  const conexaoManagerApproval = useIsDateActive(phaseIds['CONEXAO_MANAGER_APPROVAL']);
  const conexaoConfirmation = useIsDateActive(phaseIds['CONEXAO_CONFIRMATION']);
  // ETAPA_1_PREMIO ausente em PREMIO_ELBB → false
  const etapa1premio = useIsDateActive(phaseIds['ETAPA_1_PREMIO']);
  const etapa2premio = useIsDateActive(phaseIds['ETAPA_2_PREMIO']);
  const etapa3premio = useIsDateActive(phaseIds['ETAPA_3_PREMIO']);

  return useMemo(() => ({
    elbbPreRegistration,
    elbbRegistrationPeriod,
    elbbConfirmation,
    elbbInscriptionPeriod,
    elbbVotingStage1,
    elbbVotingStage2,
    conexaoPreRegistration,
    conexaoRegistrationPeriod,
    conexaoManagerApproval,
    conexaoConfirmation,
    etapa1premio,
    etapa2premio,
    etapa3premio,
    isAnyElbbActive: elbbPreRegistration || elbbRegistrationPeriod || elbbConfirmation,
    canAccessCadastroELBB: elbbRegistrationPeriod || elbbPreRegistration || elbbConfirmation,
    isAnyConexaoActive: conexaoPreRegistration || conexaoRegistrationPeriod || conexaoManagerApproval || conexaoConfirmation,
    canAccessCadastroConexao: conexaoPreRegistration, // Mudado para etapa_pre_conexao
  }), [
    elbbPreRegistration, elbbRegistrationPeriod, elbbConfirmation, elbbInscriptionPeriod,
    elbbVotingStage1, elbbVotingStage2,
    conexaoPreRegistration, conexaoRegistrationPeriod, conexaoManagerApproval, conexaoConfirmation,
    etapa1premio, etapa2premio, etapa3premio,
  ]);
}
